import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import Badge from '../components/Badge';
import SLAIndicator from '../components/SLAIndicator';
import Button from '../components/Button';
import { fmt, apiErrorMessage } from '../utils/helpers';
import { hasRole } from '../utils/roleUtils';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import type { Ticket } from '../types';

interface HistoryTicket extends Ticket {
  myAction?: string | null;
  myActionAt?: string | null;
  myActionNote?: string | null;
}

function historyStatusBadge(status: string): { label: string; cls: string } {
  switch (status) {
    case 'PendingITManager':     return { label: 'Awaiting IT Manager', cls: 'bg-amber-50 text-amber-700 border border-amber-100' };
    case 'PendingLineManager':   return { label: 'Awaiting Line Manager', cls: 'bg-amber-50 text-amber-700 border border-amber-100' };
    case 'PendingTopManagement': return { label: 'Forwarded — awaiting Top Management', cls: 'bg-amber-50 text-amber-700 border border-amber-100' };
    case 'Approved': case 'Assigned': case 'InProgress': return { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-100' };
    case 'Completed': case 'Closed': case 'ClosedCompleted': return { label: 'Completed ✓', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-100' };
    case 'Rejected': case 'ClosedCannotImplement': case 'ClosedOutOfScope': case 'ClosedDuplicate': return { label: 'Rejected ✗', cls: 'bg-red-50 text-red-600 border border-red-100' };
    default: return { label: status, cls: 'bg-gray-50 text-gray-500 border border-gray-100' };
  }
}

const PRIORITY_TEXT: Record<string, string> = {
  Critical: 'text-red-700', High: 'text-orange-600', Medium: 'text-yellow-600', Low: 'text-green-700',
};

export default function Approvals() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { toast } = useToast();

  const [awaiting,     setAwaiting]     = useState<Ticket[]>([]);
  const [awaitingLoad, setAwaitingLoad] = useState(true);
  const [previously,   setPreviously]   = useState<HistoryTicket[]>([]);
  const [previousLoad, setPreviousLoad] = useState(true);

  const [working,  setWorking]  = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});

  // My approval history is a finished record of a decision already made — it shouldn't
  // drop the viewer onto the live ticket page (with its own assign/action panels that
  // may or may not apply to them anymore). A read-only summary of the approval itself
  // is enough.
  const [historyDetail, setHistoryDetail] = useState<HistoryTicket | null>(null);

  const refetchAwaiting = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setAwaitingLoad(true);
    api.get<Ticket[]>('/tickets/pending/approvals')
      .then(r => setAwaiting(r.data))
      .catch(() => { if (!opts?.silent) setAwaiting([]); })
      .finally(() => { if (!opts?.silent) setAwaitingLoad(false); });
  }, []);

  const refetchPreviously = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setPreviousLoad(true);
    api.get<Ticket[]>('/tickets/previously-approved')
      .then(r => setPreviously(r.data))
      .catch(() => { if (!opts?.silent) setPreviously([]); })
      .finally(() => { if (!opts?.silent) setPreviousLoad(false); });
  }, []);

  // Poll so an approval/rejection made elsewhere (or by another manager) shows up
  // here without a manual refresh.
  useEffect(() => {
    refetchAwaiting();
    refetchPreviously();
    const interval = setInterval(() => {
      refetchAwaiting({ silent: true });
      refetchPreviously({ silent: true });
    }, 15000);
    return () => clearInterval(interval);
  }, [refetchAwaiting, refetchPreviously]);

  // IT Manager approving here (PendingITManager) forwards to the Line Manager, or
  // straight to Approved if there's none to forward to (Top Management requester,
  // or no line manager on file). Line Manager approving finalizes to Approved,
  // landing in the Workspace Unassigned Queue for IT to triage/assign.
  const doApprove = async (id: string) => {
    setWorking(id);
    try {
      await api.put(`/tickets/${id}/approve`, { comment: comments[id] || '' });
      toast('Ticket approved', 'success');
      refetchAwaiting();
      refetchPreviously();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to approve'), 'error');
    } finally { setWorking(null); }
  };

  const doReject = async (id: string) => {
    if (!comments[id]?.trim()) { toast('Rejection reason is required', 'error'); return; }
    setWorking(id);
    try {
      await api.put(`/tickets/${id}/reject`, { comment: comments[id] });
      toast('Ticket rejected', 'success');
      refetchAwaiting();
      refetchPreviously();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to reject'), 'error');
    } finally { setWorking(null); }
  };

  // Available at the IT Manager's first-stage security/compliance review only —
  // send it back to the requester before it ever reaches the Line Manager.
  const doClarify = async (id: string) => {
    if (!comments[id]?.trim()) { toast('Please enter the clarification needed', 'error'); return; }
    setWorking(id);
    try {
      await api.put(`/tickets/${id}/request-clarification`, { note: comments[id] });
      toast('Clarification requested', 'success');
      refetchAwaiting();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to request clarification'), 'error');
    } finally { setWorking(null); }
  };

  // Escalation ("another round of approval" with Top Management) is available
  // alongside Approve/Reject at either stage — the Line Manager's own decision at
  // PendingLineManager, or IT Manager's own decision at PendingITManager.
  const doTransfer = async (id: string) => {
    setWorking(id);
    try {
      await api.put(`/tickets/${id}/transfer-top-mgmt`, { comment: comments[id] || '' });
      toast('Transferred to Top Management', 'success');
      refetchAwaiting();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to transfer'), 'error');
    } finally { setWorking(null); }
  };

  const renderCard = (t: Ticket) => (
    <div key={t.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="space-y-1 flex-1 min-w-0">
          <button onClick={() => navigate(`/tickets/${t.id}`, { state: { activeNav: '/approvals' } })} className="font-mono text-xs text-violet-700 hover:underline">
            {t.id}
          </button>
          <p className="text-sm font-medium text-[#010E23]">{t.requester?.name || '—'}</p>
          <div className="flex items-center gap-2">
            <Badge status={t.status} small />
            <span className={`text-xs font-medium ${PRIORITY_TEXT[t.priority] ?? 'text-gray-500'}`}>{t.priority}</span>
            <span className="text-xs text-gray-400">{fmt(t.submittedAt)}</span>
          </div>
          <p className="text-xs text-gray-500">
            {t.items?.[0]?.category}{t.items && t.items.length > 1 ? ` +${t.items.length - 1} more` : ''}
          </p>
        </div>
        <div className="w-full sm:w-[180px] sm:min-w-[180px] sm:shrink-0">
          <SLAIndicator ticket={t} />
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-2">
        <textarea
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
          rows={2}
          placeholder="Comment (required for rejection)…"
          value={comments[t.id] || ''}
          onChange={e => setComments(prev => ({ ...prev, [t.id]: e.target.value }))}
        />
        <div className="flex gap-2 flex-wrap">
          <Button variant="success" size="sm" disabled={working === t.id} onClick={() => doApprove(t.id)}>
            {working === t.id ? '…' : 'Approve'}
          </Button>
          <Button variant="danger" size="sm" disabled={working === t.id} onClick={() => doReject(t.id)}>
            {working === t.id ? '…' : 'Reject'}
          </Button>
          {(hasRole(user, 'it_manager') || hasRole(user, 'admin')) && t.status === 'PendingITManager' && (
            <Button variant="ghost" size="sm" disabled={working === t.id} onClick={() => doClarify(t.id)}>
              {working === t.id ? '…' : 'Request Clarification'}
            </Button>
          )}
          {((hasRole(user, 'line_manager') && t.status === 'PendingLineManager'
              && (!t.currentPendingWithId || t.currentPendingWithId === user?.id))
            || ((hasRole(user, 'it_manager') || hasRole(user, 'admin')) && t.status === 'PendingITManager')) && (
            <Button variant="secondary" size="sm" disabled={working === t.id} onClick={() => doTransfer(t.id)}>
              {working === t.id ? '…' : '⬆ Transfer to Top Mgmt'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate(`/tickets/${t.id}`, { state: { activeNav: '/approvals' } })}>View Detail</Button>
        </div>
      </div>
    </div>
  );

  const renderHistoryCard = (t: HistoryTicket) => {
    const myAction    = t.myAction ?? '';
    const myActionAt  = t.myActionAt ? fmt(t.myActionAt) : '—';
    const isApproved  = myAction.startsWith('Approved') || myAction.startsWith('Assigned') || myAction.startsWith('Self-assigned');
    const isRejected  = myAction === 'Rejected' || myAction.startsWith('Closed');
    const { label: currentLabel, cls: currentCls } = historyStatusBadge(t.status);

    const actionChip = isApproved
      ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">✓ Approved</span>
      : isRejected
        ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-100">✕ Closed</span>
        : <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-100">{myAction.slice(0, 25)}</span>;

    return (
      <div key={t.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setHistoryDetail(t)}
                className="font-mono text-xs text-violet-700 hover:underline shrink-0">{t.id}</button>
              {actionChip}
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${currentCls}`}>{currentLabel}</span>
            </div>
            <p className="text-sm font-medium text-[#010E23]">{t.requester?.name || '—'}</p>
            <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
              <span>{t.items?.[0]?.category}{t.items && t.items.length > 1 ? ` +${t.items.length - 1} more` : ''}</span>
              <span>·</span>
              <span className={`font-medium ${PRIORITY_TEXT[t.priority] ?? 'text-gray-500'}`}>{t.priority}</span>
              <span>·</span>
              <span>Actioned {myActionAt}</span>
            </div>
            {isRejected && t.myActionNote && (
              <p className="text-xs text-red-500 italic mt-1">Reason: {t.myActionNote}</p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setHistoryDetail(t)}>Details</Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <h1 className="text-lg font-bold text-[#010E23]">Approvals</h1>

      {/* Whatever's pending the viewer's own decision — IT Manager's first-stage
          security/compliance review, Line Manager's necessity approval, or Top
          Management's escalation approval. */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-500">
          Awaiting my approval ({awaitingLoad ? '…' : awaiting.length})
        </h2>
        {awaitingLoad ? (
          <div className="text-sm text-gray-400 py-4 text-center">Loading…</div>
        ) : awaiting.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 py-8 text-center text-sm text-gray-400">
            No pending approvals
          </div>
        ) : (
          awaiting.map(t => renderCard(t))
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-500">
          My approval history ({previousLoad ? '…' : previously.length})
        </h2>
        {previousLoad ? (
          <div className="text-sm text-gray-400 py-4 text-center">Loading…</div>
        ) : previously.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 py-8 text-center text-sm text-gray-400">
            No approval history yet
          </div>
        ) : (
          previously.map(t => renderHistoryCard(t))
        )}
      </div>

      {/* ── Approval History Detail (read-only — no live ticket action page) ─── */}
      {historyDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-bold text-[#010E23]">Approval Details</h2>
              <span className="font-mono text-xs text-violet-700">{historyDetail.id}</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-gray-400">Requester</span>
                <span className="font-medium text-[#010E23] text-right">{historyDetail.requester?.name || '—'}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-400">Category</span>
                <span className="text-right">{historyDetail.items?.[0]?.category || '—'}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-400">Priority</span>
                <span className="text-right">{historyDetail.priority}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-400">My Action</span>
                <span className="text-right">{historyDetail.myAction || '—'}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-400">Actioned</span>
                <span className="text-right">{historyDetail.myActionAt ? fmt(historyDetail.myActionAt) : '—'}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-400">Current Status</span>
                <span className="text-right">{historyStatusBadge(historyDetail.status).label}</span>
              </div>
              {historyDetail.myActionNote && (
                <div className="pt-1">
                  <p className="text-xs text-gray-400 mb-1">Note</p>
                  <p className="text-gray-700 italic">“{historyDetail.myActionNote}”</p>
                </div>
              )}
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="ghost" size="sm" onClick={() => setHistoryDetail(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
